import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { fetchClients, fetchFilters } from "../services/clients.service"
import { useEffect, useMemo, useRef, useState } from "react"
import { NumberParam, StringParam, useQueryParam } from "use-query-params"
import Swal from "sweetalert2"
import { useAuth } from "../../../context/auth-user"

export const BATCH_SIZE = 20

export const useClientsTable = () => {
    const { user } = useAuth()
    const userLogged = user?.selectedProfile
    
    const [currentPage, setCurrentPage] = useQueryParam("page", NumberParam)
    
    const [searchValue, setSearchValue] = useState("")
    const [searchFilter, setSearchFilter] = useQueryParam("clients-search", StringParam);
    
    const [statusFilter, setStatusFilter] = useQueryParam("clients-status", StringParam);
    const [industryFilter, setIndustryFilter] = useQueryParam("clients-industry", StringParam);
    const [locationFilter, setLocationFilter] = useQueryParam("clients-location", StringParam);
    
    const [sortField, setSortField] = useQueryParam("clients-sort-field", StringParam);
    const [sortOrder, setSortOrder] = useQueryParam("clients-sort-order", StringParam);

    const [showResetFilters, setShowResetFilters] = useState(() => {
        return !!(searchFilter || statusFilter || industryFilter || locationFilter);
    });
    
    const [filterOptions, setFilterOptions] = useState(() => {
        return {
            status: statusFilter ? statusFilter.split(",") : [],
            industry: industryFilter ? industryFilter.split(",") : [],
            location: locationFilter ? locationFilter.split(",") : [],
        }
    })
    
    const activeSort = useMemo(() => ({
        field: sortField || "createdAt",
        order: sortOrder || "ASC",
    }), [sortField, sortOrder])
    
    const setActiveSort = (newSort) => {
        setSortField(newSort.field)
        setSortOrder(newSort.order)
    }

    const [openFilter, setOpenFilter] = useState(null)

    const [showSortMenu, setShowSortMenu] = useState(false)
        
    const sortMenuRef = useRef(null)
    const filterRef = useRef(null)

    const clientFiltersQueryKey = ["filters-list"]
    const queryKey = ["clients-table", searchFilter, statusFilter, industryFilter, locationFilter, activeSort, currentPage ?? 1]
    
    const {
        data: filters,
        isLoading: isLoadingFilters,
        isError: isErrorFilters,
        refetch: refetchFilters,
    } = useQuery({
        queryKey: clientFiltersQueryKey,
        queryFn: fetchFilters,
        staleTime: 1000 * 60 * 5,
        retry: 2,
        refetchOnMount: true,
    })
    
    const {
        data,
        isLoading,
        isFetching,
        isError,
        refetch,
    } = useQuery({
        queryKey,
        queryFn: () => 
            fetchClients({ 
                rol: userLogged?.rol,
                page: currentPage ?? 1,
                limit: BATCH_SIZE,
                sort: activeSort,
                search: searchFilter || "",
                status: statusFilter || "",
                industry: industryFilter || "",
                location: locationFilter || "",
            }),
        staleTime: 1000 * 60 * 5, // 5 minutes
        enabled: !!userLogged,
        retry: 2,
        refetchOnMount: true,
        placeholderData: keepPreviousData, // Mantener datos anteriores mientras carga la nueva página
    })
    
    useEffect(() => {
        const closeMenusFilters = (e) => {
            if (!filterRef?.current?.contains(e.target)) {
                setOpenFilter(null)
            }
    
            if (!sortMenuRef?.current?.contains(e.target)) {
                setShowSortMenu(false)
            }
        }

        document.body.addEventListener("mousedown", closeMenusFilters)

        return () => {
            document.body.removeEventListener("mousedown", closeMenusFilters)
        } 
    }, [])

    useEffect(() => {
        Swal.fire({
            footer: 'Loading, please wait',
            showConfirmButton: false,
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => {
                Swal.showLoading()
            },
        })

        if (!isLoading) {
            Swal.close()
        }
    }, [isLoading])
    

    const handlePageChange = (page) => {
        if (page <= 1) return setCurrentPage(undefined)
        
        setCurrentPage(page)
    }

    // method to open/close filter menus
    const handleOpenFilter = (e, filterName) => {
        filterRef.current = e.target

        setOpenFilter(prev => {
            if (prev === filterName) return null

            return filterName
        })
    }

    const handleShowSortMenu = () => {
        setShowSortMenu(prev => !prev)
    }

    // method to apply selected filters from the filter menus
    const handleApplyFilter = (filterName) => {
        setCurrentPage(undefined) // Reset a página 1 cuando se aplica un filtro
        
        if (filterName === "Status") {
            setStatusFilter(filterOptions.status.length > 0 ? filterOptions.status.join(",") : undefined)
        }
        
        if (filterName === "Industry") {
            setIndustryFilter(filterOptions.industry.length > 0 ? filterOptions.industry.join(",") : undefined)
        }
        
        if (filterName === "Location") {
            setLocationFilter(filterOptions.location.length > 0 ? filterOptions.location.join(",") : undefined)
        }

        setShowResetFilters(true)
    }

    // method to clear selected filters from the filter menus
    const handleClearFilter = (filterName) => {
        if (filterName === "Status") {
            setStatusFilter(undefined)
            setFilterOptions(prev => ({ ...prev, status: [] }))
        }

        if (filterName === "Industry") {
            setIndustryFilter(undefined)
            setFilterOptions(prev => ({ ...prev, industry: [] }))
        }

        if (filterName === "Location") {
            setLocationFilter(undefined)
            setFilterOptions(prev => ({ ...prev, location: [] }))
        }

        // check if other filters are empty to hide reset all button
        const otherFiltersEmpty = Object.entries(filterOptions)
            .filter(([key]) => key !== filterName.toLocaleLowerCase())
            .every(([, values]) => values.length === 0);

        if (otherFiltersEmpty) {
            setShowResetFilters(false);
        }
    }

    // method to select/deselect filter options in the filter menus
    const handleSelectFilterOption = (filterType, value) => {
        setFilterOptions(prev => {
            const updatedOptions = { ...prev }

            if (updatedOptions[filterType].includes(value)) {
                updatedOptions[filterType] = updatedOptions[filterType].filter(item => item !== value)
            } else {
                updatedOptions[filterType].push(value)
            }
            return updatedOptions
        })
    }

    const handleSortChange = (fieldName, order) => {
        setCurrentPage(undefined) // Reset a página 1 cuando cambia el sort
        
        let newOrder = order || "ASC"
        
        if (activeSort.field === fieldName && !order) {
            newOrder = activeSort.order === "ASC" ? "DSC" : "ASC"
        }

        setActiveSort({
            field: fieldName,
            order: newOrder,
        })
    }

    const handleSearchChange = (e) => {
        const value = e.target.value.trim()

        setSearchValue(value)
    }

    const handleSetSearchFilter = (e) => {
        e.preventDefault();
        
        setCurrentPage(undefined) // Reset a página 1 cuando se hace búsqueda
        setSearchFilter(searchValue || undefined)

        setStatusFilter(undefined)
        setIndustryFilter(undefined)
        setLocationFilter(undefined)

        setFilterOptions({
            status: [],
            industry: [],
            location: [],
        })
    }

    const handleResetAllFilters = () => {
        setCurrentPage(undefined) // Reset a página 1 cuando se resetean los filtros
        
        setSearchFilter(undefined)
        setStatusFilter(undefined)
        setIndustryFilter(undefined)
        setLocationFilter(undefined)

        setFilterOptions({
            status: [],
            industry: [],
            location: [],
        })

        setShowResetFilters(false)
    }
    
    const allClients = data?.clients || []
    const totalPages = data?.totalPages || 0
    const totalCount = data?.totalCount || 0

    const dataToReturn = {
        data: allClients,
        isLoading,
        isFetching,
        isError,
        refetch,
        currentPage,
        totalPages,
        totalCount,
        handlePageChange,
        

        // filters data
        locationsData: filters?.locations || [],
        industriesData: filters?.industries || [],
        isLoadingFilters,
        isErrorFilters,
        refetchFilters,

        // handlers and states
        openFilter,
        filterOptions,
        handleOpenFilter,
        handleSelectFilterOption,
        handleApplyFilter,
        handleClearFilter,

        handleShowSortMenu,
        showSortMenu,
        handleSortChange,
        
        searchValue,
        handleSearchChange,
        handleSetSearchFilter,

        sortMenuRef,

        showResetFilters,
        handleResetAllFilters
    }

    return dataToReturn
}
